import { isBreadWithFilling } from "../desktop/catalog";

const positiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

const vietnamClock = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "Asia/Ho_Chi_Minh", year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
});

export const getPromotionPeriod = (promotion) => {
  const datePart = (value) => {
    if (!value) return "";
    return String(value).match(/^\d{4}-\d{2}-\d{2}/)?.[0] || null;
  };
  // SQL TIME is serialized with a placeholder UTC date; keep its literal clock.
  const timePart = (value) => {
    if (!value) return "";
    const match = String(value).match(/(?:^|T)(\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!match || Number(match[1]) > 23 || Number(match[2]) > 59 || Number(match[3] || 0) > 59) return null;
    return `${match[1]}:${match[2]}:${match[3] || "00"}`;
  };
  return {
    startDate: datePart(promotion.NgayBatDau), endDate: datePart(promotion.NgayKetThuc),
    startTime: timePart(promotion.GioBatDau), endTime: timePart(promotion.GioKetThuc),
  };
};

export const isPromotionAvailable = (promotion, now = new Date()) => {
  if ([false, 0, "false", "False", "0"].includes(promotion.Act_UD)) return false;
  const period = promotion.period || getPromotionPeriod(promotion);
  if (Object.values(period).some((value) => value === null)) return false;
  const parts = Object.fromEntries(vietnamClock.formatToParts(now).map(({ type, value }) => [type, value]));
  const date = `${parts.year}-${parts.month}-${parts.day}`;
  const time = `${parts.hour}:${parts.minute}:${parts.second}`;
  return (!period.startDate || date >= period.startDate) && (!period.endDate || date <= period.endDate) &&
    (!period.startTime || time >= period.startTime) && (!period.endTime || time <= period.endTime);
};

export const getGiftDrinkAllowance = (items, promotions, now = new Date()) => {
  const activeIds = new Set(promotions.filter((promotion) => isPromotionAvailable(promotion, now))
    .map((promotion) => Number(promotion.MaUD)));
  let cakeAmount = 0;
  let breadQuantity = 0;
  items.forEach((item) => {
    if (item.isPromo || Number(item.KmaiPT) === 100) return;
    const price = positiveNumber(item.price);
    const quantity = positiveNumber(item.quantity);
    if (isBreadWithFilling(item)) {
      if (price >= 20000) breadQuantity += Math.floor(quantity);
      return;
    }
    const category = String(item.category || item.categoryId || item.MaNLSP || "").toUpperCase();
    const isCake = /^(BANH|BMKN|KEM)/.test(category) ||
      (/^\d{1,4}$/.test(category) && Number(category) >= 2 && Number(category) <= 12);
    // Exclude drinks, groceries and addons from the cake spend.
    if (isCake && !item._addonParent) cakeAmount += price * quantity;
  });
  return {
    9: activeIds.has(9) ? breadQuantity : 0,
    13: activeIds.has(13) ? Math.floor(cakeAmount / 30000) : 0,
  };
};

export const buildGiftDrinkOptions = (details) => {
  const options = new Map();
  details.forEach(({ promotionId, rows }) => {
    if (!Array.isArray(rows) || rows.some((row) => !Array.isArray(row.items))) {
      throw new Error("Danh sách nước tặng không hợp lệ");
    }
    rows.forEach((row) => row.items.forEach((item) => {
      if (item.MaSP === undefined || item.MaSP === null) return;
      const key = String(item.MaSP);
      const existing = options.get(key);
      if (existing) {
        if (!existing.promotionIds.includes(promotionId)) existing.promotionIds.push(promotionId);
      } else {
        options.set(key, {
          id: item.MaSP,
          name: item.Ten_SP || item.TenSP || key,
          price: positiveNumber(item.DonGia ?? item.Gia),
          unit: item.DVT_SP || item.DVT || "",
          category: item.MaNLSP || "CAFE",
          vat: item.VAT_SP || 0,
          promotionIds: [promotionId],
        });
      }
    }));
  });
  return [...options.values()];
};

export const sumGiftDrinks = (selections) =>
  Object.values(selections).reduce((sum, quantity) => sum + Math.floor(positiveNumber(quantity)), 0);

export const reconcileGiftDrinks = (selections, options, allowance) => {
  const remaining = { ...allowance };
  const result = {};
  // Reserve exclusive options first so a shared drink cannot consume their quota.
  const entries = Object.entries(selections).map(([key, quantity]) => ({
    key, quantity: Math.floor(positiveNumber(quantity)),
    option: options.find((item) => String(item.id) === key),
  })).filter((entry) => entry.option && entry.quantity > 0)
    .sort((a, b) => a.option.promotionIds.length - b.option.promotionIds.length);
  entries.forEach(({ key, quantity, option }) => {
    let needed = quantity;
    option.promotionIds.forEach((id) => {
      const used = Math.min(needed, remaining[id] || 0);
      remaining[id] -= used;
      needed -= used;
    });
    if (quantity > needed) result[key] = quantity - needed;
  });
  return result;
};

export const buildGiftDrinkItems = (selections, options) => options
  .filter((option) => selections[String(option.id)] > 0)
  .map((option) => ({
    ...option,
    quantity: selections[String(option.id)],
    isPromo: true,
    KmaiPT: 100,
    ThanhTien: 0,
    MXTY: 2,
    note: "Tặng kèm khuyến mãi",
  }));
